import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, MessageCircle, Check } from 'lucide-react';
import liff from '@line/liff';
import { api, Staff, Store } from '../api';
import { liffId } from '../firebase';
import Logo from '../components/Logo';

const LINE_FRIEND_ADD_URL = 'https://line.me/R/ti/p/@471wngia';
// 動作確認（エミュレータ）用：LINEログインを省略して仮のLINEアカウントで連携する
const LIFF_MOCK = import.meta.env.VITE_LIFF_MOCK === 'true';

// 招待URLの受け付け画面。
// LINEを使う店舗：LINEでログイン（LIFF）し、LINEアカウントを連携してそのままログインする。
// LINEを使わない店舗：パスワードを設定してログインアカウントを作成する。
const InvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'success'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [staff, setStaff] = useState<Staff | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  const [lineEnabled, setLineEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [lineProfile, setLineProfile] = useState<{ userId: string; displayName: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setStatus('error');
        setErrorMsg('招待URLが正しくありません。URLを再度ご確認ください。');
        return;
      }
      try {
        const ctx = await api.getInviteContext(token);
        if (!ctx) {
          setStatus('error');
          setErrorMsg('この招待URLは無効です。既に登録済みか、新しい招待URLが発行されている可能性があります。');
          return;
        }
        setStaff(ctx.staff);
        setStore(ctx.store);
        setLineEnabled(ctx.lineEnabled);
        if (ctx.lineEnabled) {
          if (LIFF_MOCK) {
            setLineProfile({ userId: `Umock_${ctx.staff.id}`, displayName: `${ctx.staff.lastName} ${ctx.staff.firstName}` });
          } else {
            // LINEログイン（未ログインならLINEのログイン画面へ移動し、この画面に戻る）
            if (!liffId) throw new Error('LIFF ID設定エラー');
            await liff.init({ liffId });
            if (!liff.isLoggedIn()) { liff.login({ redirectUri: window.location.href }); return; }
            const profile = await liff.getProfile();
            setLineProfile({ userId: profile.userId, displayName: profile.displayName });
          }
        }
        setStatus('ready');
      } catch (e) {
        setStatus('error');
        setErrorMsg('情報の取得に失敗しました。時間をおいて再度お試しください。');
      }
    };
    load();
  }, [token]);

  const goNext = async (s: Staff) => {
    setStatus('success');
    // LINEで友だち追加していない場合は、通知を受け取れるよう友だち追加画面を開く
    if (lineEnabled && !LIFF_MOCK) {
      try { if (liff.isInClient() && !(await liff.getFriendship()).friendFlag) liff.openWindow({ url: LINE_FRIEND_ADD_URL, external: false }); } catch { /* ブラウザでは確認できない */ }
    }
    const isManager = s.role === 'system_admin' || s.role === 'store_manager';
    setTimeout(() => navigate(isManager ? (s.setupCompleted ? '/admin/shifts' : '/admin/setup') : '/user/shifts', { replace: true }), 1500);
  };

  const handleLinkLine = async () => {
    if (!staff || !token || !lineProfile) return;
    setIsSubmitting(true);
    try {
      await api.linkLineAccount({ token, accessToken: LIFF_MOCK ? `mock:${lineProfile.userId}` : (liff.getAccessToken() || '') });
      goNext(staff);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : '連携に失敗しました。');
      setIsSubmitting(false);
    }
  };

  const rules = {
    length: password.length >= 8,
    mixed: /[a-zA-Z]/.test(password) && /[0-9]/.test(password),
    match: password.length > 0 && password === passwordConfirm,
  };
  const canCreate = rules.length && rules.mixed && rules.match;

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !canCreate) return;
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      const s = await api.acceptAccountInvite(token, password);
      goNext(s);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'アカウントの作成に失敗しました。');
      setIsSubmitting(false);
    }
  };

  const Rule: React.FC<{ ok: boolean; text: string }> = ({ ok, text }) => (
    <div className={`flex items-center gap-2 text-xs ${ok ? 'text-blue-600 font-bold' : 'text-slate-400'}`}>
      <span className={`w-4 h-4 rounded-[2px] border flex items-center justify-center ${ok ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 text-transparent'}`}><Check size={10} strokeWidth={4} /></span>
      {text}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center">
      <div className="w-full max-w-[480px] bg-white min-h-screen shadow-md px-6 py-10 flex flex-col">
        <div className="flex justify-center mb-10"><Logo size="md" /></div>

        {status === 'loading' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <Loader2 className="animate-spin text-blue-600" size={32} />
            <p className="text-sm text-slate-500 font-bold">招待内容を確認しています。</p>
          </div>
        )}

        {status === 'error' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <AlertCircle className="text-red-500" size={40} />
            <p className="text-sm text-slate-600 font-bold leading-relaxed">{errorMsg}</p>
            <button onClick={() => navigate('/login')} className="mt-4 text-sm font-bold text-blue-600 hover:underline">ログイン画面へ</button>
          </div>
        )}

        {status === 'success' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <CheckCircle2 className="text-green-600" size={48} />
            <h1 className="text-xl font-bold text-slate-800">{lineEnabled ? 'LINEの連携が完了しました。' : 'アカウントを作成しました。'}</h1>
            <p className="text-sm text-slate-500 font-bold leading-relaxed">
              {lineEnabled ? 'シフトの提出依頼や確定のお知らせがLINEに届きます。' : 'シフトの提出依頼や確定のお知らせは、メールとアプリ内で届きます。'}
            </p>
          </div>
        )}

        {status === 'ready' && staff && (
          <div className="flex-1 flex flex-col" data-testid={lineEnabled ? 'invite-line' : 'invite-account'}>
            <p className="text-xs text-slate-500 font-bold text-center mb-1">{store?.companyName}　{store?.name}</p>
            <h1 className="text-xl font-bold text-slate-800 text-center mb-2">{staff.lastName} {staff.firstName}さん、ようこそ。</h1>
            <p className="text-sm text-slate-500 text-center leading-relaxed mb-8">
              {lineEnabled
                ? 'LINEを連携すると、シフトの提出や確認をLINEから行えます。'
                : 'パスワードを設定して、ログインアカウントを作成してください。'}
            </p>

            {errorMsg && <p className="text-sm text-red-500 font-bold text-center mb-4">{errorMsg}</p>}

            {lineEnabled ? (
              <div className="space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-[11px] text-slate-400 font-bold mb-1">連携するLINEアカウント</p>
                  <p className="text-sm font-bold text-slate-800">{lineProfile?.displayName || `${staff.lastName} ${staff.firstName}`}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{store?.name}</p>
                </div>
                <button onClick={handleLinkLine} disabled={isSubmitting} className="w-full py-4 bg-[#06C755] text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:brightness-105 active:scale-95 transition-all disabled:opacity-60">
                  {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <><MessageCircle size={20} />LINEで連携する</>}
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block">ログインID（メールアドレス）</label>
                  <input type="email" value={staff.email || ''} readOnly className="w-full border border-slate-200 bg-slate-50 rounded-[4px] px-3 py-2.5 text-sm font-bold text-slate-600" />
                </div>
                <div>
                  <label htmlFor="invite-password" className="text-xs font-bold text-slate-500 mb-1 block">パスワード</label>
                  <input id="invite-password" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" className="w-full border border-slate-300 rounded-[4px] px-3 py-2.5 text-sm font-bold focus:ring-2 focus:ring-blue-100 outline-none" />
                </div>
                <div>
                  <label htmlFor="invite-password-confirm" className="text-xs font-bold text-slate-500 mb-1 block">パスワード（確認）</label>
                  <input id="invite-password-confirm" type="password" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} autoComplete="new-password" className="w-full border border-slate-300 rounded-[4px] px-3 py-2.5 text-sm font-bold focus:ring-2 focus:ring-blue-100 outline-none" />
                </div>
                <div className="space-y-1.5">
                  <Rule ok={rules.length} text="8文字以上" />
                  <Rule ok={rules.mixed} text="英字と数字を含む" />
                  <Rule ok={rules.match} text="確認用と一致" />
                </div>
                <button type="submit" disabled={!canCreate || isSubmitting} className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all disabled:bg-slate-300">
                  {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : 'アカウントを作成する'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitePage;
